import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { works } from '@/mocks/works';

interface SearchBarProps {
  initialValue?: string;
  size?: 'sm' | 'lg';
  onSubmit?: (value: string) => void;
  showSuggestions?: boolean;
}

export default function SearchBar({ initialValue = '', size = 'sm', onSubmit, showSuggestions = false }: SearchBarProps) {
  const [value, setValue] = useState(initialValue);
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const keyword = value.trim();
  const suggestions =
    showSuggestions && keyword
      ? works
          .filter(
            (work) =>
              work.title.includes(keyword) ||
              work.actors.some((actor) => actor.includes(keyword)) ||
              work.genres.some((genre) => genre.includes(keyword)),
          )
          .slice(0, 6)
      : [];

  const showPanel = focused && suggestions.length > 0;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!keyword) return;
    if (onSubmit) {
      onSubmit(keyword);
      return;
    }
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
  };

  const goToWork = (workId: string) => {
    setFocused(false);
    navigate(`/detail/${workId}`);
  };

  const isLarge = size === 'lg';

  return (
    <div className="relative w-full">
      <form
        onSubmit={handleSubmit}
        role="search"
        className={`flex w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 transition-colors focus-within:border-primary-400 ${
          isLarge ? 'py-1 pl-4 pr-1' : 'py-0.5 pl-3 pr-0.5'
        }`}
      >
        <i className={`ri-search-line shrink-0 text-foreground-500 ${isLarge ? 'text-lg' : 'text-base'}`} />
        <input
          name="q"
          type="search"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onFocus={() => {
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
            setFocused(true);
          }}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => setFocused(false), 150);
          }}
          placeholder="搜索影视名称、主演…"
          className={`min-w-0 flex-1 bg-transparent text-foreground-950 placeholder:text-foreground-500 focus:outline-none ${
            isLarge ? 'py-2 text-base' : 'py-1.5 text-sm'
          }`}
        />
        <button
          type="submit"
          className={`flex shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full bg-primary-500 font-medium text-background-50 transition-colors hover:bg-primary-600 ${
            isLarge ? 'h-10 px-5 text-sm' : 'h-8 px-4 text-sm'
          }`}
        >
          <i className="ri-search-line sm:hidden" />
          <span className="hidden sm:inline">搜索</span>
        </button>
      </form>

      {showPanel && (
        <ul className="absolute left-0 right-0 top-full z-40 mt-2 max-h-80 overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
          {suggestions.map((work) => (
            <li key={work.id}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => goToWork(work.id)}
                className="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-background-100"
              >
                <span className="flex h-12 w-8 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={work.cover} alt={`《${work.title}》封面`} className="h-full w-full object-cover object-top" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground-950">{work.title}</span>
                  <span className="mt-0.5 block truncate text-xs text-foreground-500">
                    {work.year} · {work.region} · {work.actors.slice(0, 2).join(' / ')}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-primary-600">{work.type === 'tv' ? '电视剧' : '电影'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}